import React, { useState, useEffect, useMemo } from "react";
import { TrendingUp, Search, MapPin, UserPlus, Home, PoundSterling, Trash2, Wand2, Loader2 } from "lucide-react";
import { getAdminValuations, updateValuationStatus, addLeadManual, deleteValuation, dedupeValuations } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUSES = ["new", "contacted", "booked", "closed"];
const sColor = {
  new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700",
  booked: "bg-purple-100 text-purple-700", closed: "bg-green-100 text-green-700",
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
const fmtMoney = (n) => (n ? `£${Number(n).toLocaleString()}` : "—");

const AdminValuations = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const refresh = () => getAdminValuations().then((d) => setRows(d.map((v) => ({ ...v, status: v.status || "new" })))).catch(() => setRows([]));
  useEffect(() => { refresh(); }, []);

  const setStatus = async (id, status) => {
    try { const u = await updateValuationStatus(id, status); setRows((rs) => rs.map((r) => r.id === id ? { ...r, ...u, status } : r)); toast({ title: `Marked ${status}` }); }
    catch { toast({ title: "Update failed" }); }
  };

  const toCrm = async (v) => {
    try {
      await addLeadManual({ name: v.name || "Valuation Enquiry", email: v.email || "unknown@noemail.com", phone: v.phone || "", message: `Valuation for ${v.postcode?.toUpperCase()} · Est ${fmtMoney(v.estimate)}`, type: "valuation" });
      toast({ title: "Added to CRM", description: "New lead created in Leads / CRM." });
    } catch (err) { toast({ title: "Couldn't add to CRM", description: err?.response?.data?.detail || "" }); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete the valuation request for ${v.postcode?.toUpperCase() || "this property"}? This can't be undone.`)) return;
    try { await deleteValuation(v.id); setRows((rs) => rs.filter((r) => r.id !== v.id)); toast({ title: "Valuation deleted" }); }
    catch { toast({ title: "Couldn't delete valuation" }); }
  };

  const [dedup, setDedup] = useState(false);
  const dedupe = async () => {
    if (!window.confirm("Remove duplicate valuation requests (same person & address)? This can't be undone.")) return;
    setDedup(true);
    try { const r = await dedupeValuations(); toast({ title: r.removed ? `${r.removed} duplicate${r.removed !== 1 ? "s" : ""} removed` : "No duplicates found", description: `${r.remaining} requests remain.` }); refresh(); }
    catch { toast({ title: "Couldn't dedupe" }); }
    finally { setDedup(false); }
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const withEst = rows.filter((r) => r.estimate);
    const avg = withEst.length ? Math.round(withEst.reduce((s, r) => s + r.estimate, 0) / withEst.length) : 0;
    const totalVal = rows.reduce((s, r) => s + (r.estimate || 0), 0);
    const open = rows.filter((r) => r.status === "new").length;
    return { total, avg, totalVal, open };
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return [r.name, r.email, r.postcode, r.address].filter(Boolean).some((f) => f.toLowerCase().includes(s));
    });
  }, [rows, q, filter]);

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-valuations">
      <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><TrendingUp size={19} className="text-[#b8935f]" /> Valuation Requests ({rows.length})
        <button onClick={dedupe} disabled={dedup} data-testid="valuation-dedupe-btn" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors disabled:opacity-60">{dedup ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Remove duplicates</button>
      </h3>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[["Requests", stats.total, Home], ["New / to action", stats.open, TrendingUp], ["Avg estimate", fmtMoney(stats.avg), PoundSterling], ["Pipeline value", fmtMoney(stats.totalVal), PoundSterling]].map(([l, v, Icon]) => (
          <div key={l} className="p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="valuation-stat">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#a0a0a0] mb-1"><Icon size={13} className="text-[#b8935f]" /> {l}</div>
            <p className="font-display text-[22px] font-500 text-[#2b2b2b] leading-none">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0a0]" />
          <input placeholder="Search by name, email or postcode…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-[44px] pl-10 pr-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" data-testid="valuation-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] h-[44px] border-[#e2d8c8]" data-testid="valuation-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? <p className="p-10 text-center text-[#8a8a8a]">No valuation requests match.</p> : (
        <div className="space-y-2">
          {filtered.map((v) => (
            <div key={v.id} className="p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="valuation-row">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-500 text-[#2b2b2b] flex items-center gap-1.5"><MapPin size={14} className="text-[#b8935f]" /> {v.postcode?.toUpperCase()} {v.address ? `· ${v.address}` : ""}</p>
                  <p className="text-[13px] text-[#8a8a8a]">{v.name || "Anonymous"}{v.email ? ` · ${v.email}` : ""}{v.phone ? ` · ${v.phone}` : ""}</p>
                  <p className="text-[12px] text-[#a0a0a0] mt-0.5">{v.propertyType || "—"}{v.bedrooms ? ` · ${v.bedrooms} bed` : ""} · {fmtDate(v.createdAt)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="text-right mr-1">
                    <p className="text-[15px] font-500 text-[#b8935f]">{fmtMoney(v.estimate)}</p>
                    {(v.low || v.high) && <p className="text-[11px] text-[#a0a0a0]">{fmtMoney(v.low)}–{fmtMoney(v.high)}</p>}
                  </div>
                  <span className={`text-[11px] px-2.5 py-1 rounded-full capitalize ${sColor[v.status]}`}>{v.status}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Select value={v.status} onValueChange={(s) => setStatus(v.id, s)}>
                  <SelectTrigger className="w-[140px] h-9 border-[#e2d8c8]" data-testid="valuation-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => toCrm(v)} data-testid="valuation-to-crm-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><UserPlus size={14} /> Add to CRM</button>
                <button onClick={() => remove(v)} data-testid="valuation-delete-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2d8c8] text-[12px] text-[#a0a0a0] hover:border-red-300 hover:text-red-500 transition-colors ml-auto"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminValuations;
